import { useEffect, useState } from "react";
import { TrainingAPI } from "@api/TrainingAPI";

const trainingAPI = new TrainingAPI();

// ref -> Promise<objectURL|null>. Module-scoped so every caller shares one fetch per ref.
// Immutable ref → one entry per image for the session (no version bump, no revoke), exactly like
// a resolved headshot. primeChatImage seeds it with the paste thumbnail so a just-sent image
// renders with no network hit and no flash.
const cache = new Map<string, Promise<string | null>>();

function resolveChatImage(ref: string): Promise<string | null> {
    const hit = cache.get(ref);
    if (hit) return hit;
    const url = trainingAPI
        .getChatbotImageBlob(ref)
        .then((blob) => (blob ? URL.createObjectURL(blob) : null));
    cache.set(ref, url);
    return url;
}

export function primeChatImage(ref: string, objectUrl: string): void {
    // Ownership of objectUrl transfers to the cache; the caller must NOT revoke it.
    if (!cache.has(ref)) cache.set(ref, Promise.resolve(objectUrl));
}

// Tri-state so the consumer can tell loading from failure:
//   undefined = still resolving   |   null = failed (403/404/deleted)   |   string = object URL
// A primed ref resolves on a microtask, so its `undefined` window is one imperceptible frame.
// A failed read caches a null-resolving promise, so that ref won't retry until reload (module
// cache cleared) — same behaviour as useHeadshot; fine for the permanent 403/404 we expect.
export function useChatImage(ref: string | null | undefined): string | null | undefined {
    const [objectUrl, setObjectUrl] = useState<string | null | undefined>(undefined);
    useEffect(() => {
        if (!ref) {
            setObjectUrl(null);
            return;
        }
        setObjectUrl(undefined); // reset to loading when the ref changes
        let cancelled = false;
        resolveChatImage(ref).then((url) => {
            if (!cancelled) setObjectUrl(url);
        });
        return () => {
            cancelled = true;
        };
    }, [ref]);
    return objectUrl;
}
